import { useCallback, useEffect, useMemo, useState } from 'react';
import { callDeepLink } from '@croquiscom-pvt/web2app';
import getConfig from 'next/config';

import { isZigzag } from '@common/device-manager';

import { setShouldOpenDirectInput } from '../../constant/local-storage';
import { SIZE_INFO_MESSAGES } from '../../constant/messages';
import { useSizeInfoLoginRedirect } from '../../hooks/useSizeInfoLoginRedirect';
import { useSizeInquiry } from '../../hooks/useSizeInquiry';
import { useTableLayout } from '../../hooks/useTableLayout';
import { useAgreementPopup } from '../../utils/agreement-popup';
import { useAgreeTermAgreementMutation, usePdpCompareSizeMutation } from '../useSizeInfoMutation';
import { applySimilarityToItem, getFlattenedData } from './data-transform';
import { getMySizeRowProps } from './row-helper';
import { analyzeSizeSimilarity } from './similarity-analyzer';
import { SIZE_TAB, SizeInfoItem } from './types';
import { useActualSizeItemData } from './useActualSizeData';

/**
 * 개별 사이즈 아이템에 대한 전체 로직을 관리하는 훅
 * 각 size_category_id별로 독립적인 상태와 비즈니스 로직을 처리합니다.
 */
export const useActualSizeItemService = (rawItemInput: SizeInfoItem | undefined) => {
  const {
    rawItem,
    userMeasureRow,
    refetchUserMeasurement,
    isUserSizeAgreed,
    isLoggedIn,
    isLoading,
    isSizeComparable,
    activeTab,
    onTabClick,
    myPurchaseData,
    selectedPurchaseId,
    onPurchaseCardClick: handlePurchaseCardClickInternal,
    purchaseCompareRow,
    selectedPurchaseImage,
    catalogProductId,
  } = useActualSizeItemData(rawItemInput);

  const { mutate: compareSize } = usePdpCompareSizeMutation();
  const { handleSizeInquiry } = useSizeInquiry(catalogProductId);
  const { mutateAsync: agreeTermAgreement } = useAgreeTermAgreementMutation();
  const [isAgreedFromPopup, setIsAgreedFromPopup] = useState(false);
  const [pendingPurchaseId, setPendingPurchaseId] = useState<string | null>(null);

  const isAgreedFinal = isUserSizeAgreed || isAgreedFromPopup;

  const { openSizeRecommendationPopup } = useAgreementPopup(catalogProductId);
  const { loginWithRedirect } = useSizeInfoLoginRedirect();

  const flattened = getFlattenedData(rawItem);
  const columnCount = rawItem?.headers?.length ?? 0;
  const { sizeTable } = useTableLayout(flattened, columnCount);

  useEffect(() => {
    if (isAgreedFinal) {
      if (pendingPurchaseId && rawItem?.sizeCategoryId) {
        handlePurchaseCardClickInternal(pendingPurchaseId);

        const pendingPurchase = myPurchaseData?.find((item) => item.uniqueKey === pendingPurchaseId);
        compareSize({
          variables: {
            catalog_product_id: pendingPurchase?.catalog_product_id ?? '',
            size_option_name: pendingPurchase?.sizeOptionName ?? '',
            size_category_id: rawItem.sizeCategoryId,
          },
        });

        setPendingPurchaseId(null);
      }
    }
  }, [
    isAgreedFinal,
    pendingPurchaseId,
    rawItem?.sizeCategoryId,
    catalogProductId,
    compareSize,
    handlePurchaseCardClickInternal,
    myPurchaseData,
  ]);

  const handleAgreeSizeInfo = useCallback(async () => {
    setIsAgreedFromPopup(true);

    try {
      await agreeTermAgreement({
        variables: {
          input: {
            term_types: ['PROVIDING_PRODUCT_MEASUREMENT'],
          },
        },
      });
      refetchUserMeasurement();
    } catch (e) {
      // 실패해도 클라이언트 동의 상태는 유지
    }
  }, [agreeTermAgreement, refetchUserMeasurement]);

  const handleSelectMyPurchaseFromMessage = useCallback(
    (_targetCatalogId: string, uniqueKey: string) => {
      if (!uniqueKey) return;

      if (!isAgreedFinal) {
        setPendingPurchaseId(uniqueKey);
        openSizeRecommendationPopup({ type: 'compare' });
      } else {
        handlePurchaseCardClickInternal(uniqueKey);

        const selectedPurchase = myPurchaseData?.find((item) => item.uniqueKey === uniqueKey);
        if (selectedPurchase && rawItem?.sizeCategoryId) {
          compareSize({
            variables: {
              catalog_product_id: selectedPurchase.catalog_product_id,
              size_option_name: selectedPurchase.sizeOptionName ?? '',
              size_category_id: rawItem.sizeCategoryId,
            },
          });
        }
      }
    },
    [
      isAgreedFinal,
      handlePurchaseCardClickInternal,
      openSizeRecommendationPopup,
      myPurchaseData,
      rawItem?.sizeCategoryId,
      compareSize,
    ],
  );

  const handleCompareDirectInput = useCallback(() => {
    refetchUserMeasurement();
  }, [refetchUserMeasurement]);

  const isMyPurchaseTab = activeTab === SIZE_TAB.MY_PURCHASE;

  const finalCompareRow = isAgreedFinal
    ? isMyPurchaseTab
      ? purchaseCompareRow
      : userMeasureRow || purchaseCompareRow
    : undefined;

  const finalItem = useMemo(() => {
    const similarityResult = analyzeSizeSimilarity(rawItem, finalCompareRow);
    const itemWithSimilarity = applySimilarityToItem(rawItem, similarityResult, finalCompareRow);

    if (!itemWithSimilarity) return null;

    let description = [...(itemWithSimilarity.description || [])];

    const originalHasHighlightText = description.includes(SIZE_INFO_MESSAGES.GUIDE_HIGHLIGHT);
    description = description.filter(
      (d) => d !== SIZE_INFO_MESSAGES.GUIDE_HIGHLIGHT && d !== SIZE_INFO_MESSAGES.GUIDE_NO_HIGHLIGHT,
    );

    if (description.length === 0) {
      description.push(SIZE_INFO_MESSAGES.DEFAULT_DESCRIPTION);
    }

    if (isSizeComparable) {
      const hasHighlight = similarityResult?.mostSimilarIndex !== undefined && similarityResult.mostSimilarIndex !== -1;

      if (hasHighlight) {
        const wasOriginallyEmpty = (itemWithSimilarity.description || []).length === 0;
        if (wasOriginallyEmpty || originalHasHighlightText) {
          description.unshift(SIZE_INFO_MESSAGES.GUIDE_HIGHLIGHT);
        }
      } else {
        description.unshift(SIZE_INFO_MESSAGES.GUIDE_NO_HIGHLIGHT);
      }
    }

    return {
      ...itemWithSimilarity,
      description,
    };
  }, [rawItem, finalCompareRow, isSizeComparable]);

  const onCompareClick = useCallback(() => {
    const { config } = getConfig().publicRuntimeConfig;
    const directInputUrl = `${config.appUrl}/products/${catalogProductId}/direct-size-input?size_category_id=${rawItem?.sizeCategoryId}`;

    if (!isLoggedIn) {
      if (rawItem?.sizeCategoryId) {
        setShouldOpenDirectInput({
          catalogProductId,
          sizeCategoryId: rawItem.sizeCategoryId,
        });
      }

      loginWithRedirect(catalogProductId);
      return;
    }

    if (isZigzag()) {
      callDeepLink('/full_screen_simple_browser')({ url: encodeURIComponent(directInputUrl), input_accessory: true });
    } else {
      location.href = directInputUrl;
    }
  }, [isLoggedIn, catalogProductId, rawItem?.sizeCategoryId, loginWithRedirect]);

  const onPurchaseCardClick = useCallback(
    (id: string): boolean => {
      if (!isLoggedIn) {
        loginWithRedirect(catalogProductId);
        return false;
      }

      if (!isAgreedFinal) {
        setPendingPurchaseId(id);
        openSizeRecommendationPopup({ type: 'compare' });
        return false;
      }

      handlePurchaseCardClickInternal(id);

      if (rawItem?.sizeCategoryId) {
        const selectedPurchase = myPurchaseData?.find((item) => item.uniqueKey === id);
        compareSize({
          variables: {
            catalog_product_id: selectedPurchase?.catalog_product_id ?? '',
            size_option_name: selectedPurchase?.sizeOptionName ?? '',
            size_category_id: rawItem.sizeCategoryId,
          },
        });
      }

      return true;
    },
    [
      isAgreedFinal,
      isLoggedIn,
      handlePurchaseCardClickInternal,
      catalogProductId,
      rawItem?.sizeCategoryId,
      compareSize,
      openSizeRecommendationPopup,
      loginWithRedirect,
      myPurchaseData,
    ],
  );

  const uiState = useMemo(() => {
    return {
      showTabs: isSizeComparable,
      showImageArea: !!finalItem?.imageUrl,
      showMyPurchaseCarousel: isSizeComparable && isMyPurchaseTab && (myPurchaseData?.length ?? 0) > 0,
      showTable: (finalItem?.rows?.length ?? 0) > 0,
      showDescription: (finalItem?.description?.length ?? 0) > 0,
    };
  }, [finalItem, isMyPurchaseTab, isSizeComparable, myPurchaseData]);

  const mySizeRowProps = useMemo(
    () =>
      getMySizeRowProps({
        isMyPurchaseTab,
        myPurchaseData,
        selectedPurchaseId,
        userMeasureRow: userMeasureRow as string[] | undefined,
        purchaseCompareRow: purchaseCompareRow as string[] | undefined,
        isAgreedFinal,
        isSizeComparable,
        onCompareClick,
        highlightFlags: finalItem?.highlightFlags,
      }),
    [
      isMyPurchaseTab,
      myPurchaseData,
      selectedPurchaseId,
      userMeasureRow,
      purchaseCompareRow,
      isAgreedFinal,
      isSizeComparable,
      onCompareClick,
      finalItem?.highlightFlags,
    ],
  );

  return {
    visible: !!finalItem,
    item: finalItem,
    catalogProductId,
    sizeCategoryId: rawItem?.sizeCategoryId,
    sizeTable,
    activeTab,
    onTabClick,
    onCompareClick,
    isLoggedIn,
    isLoading,
    mySizeLabel: null,
    myPurchaseData,
    selectedPurchaseId,
    onPurchaseCardClick,
    selectedPurchaseImage,
    uiState,
    mySizeRowProps,
    hasCompareRow: isSizeComparable && !!finalCompareRow,
    handleSelectMyPurchaseFromMessage,
    handleCompareDirectInput,
    handleAgreeSizeInfo,
    handleSizeInquiry,
  };
};
